import { useState } from "react";
import type { Order } from "@/mocks/orders";

type Stage = "not_started" | "in_transit" | "arrived" | "executing" | "completed";

const stages: { key: Stage; label: string; icon: string }[] = [
  { key: "in_transit", label: "بدء المهمة والتوجه", icon: "ri-navigation-line" },
  { key: "arrived", label: "الوصول للعميل", icon: "ri-map-pin-2-line" },
  { key: "executing", label: "بدء التنفيذ", icon: "ri-tools-line" },
  { key: "completed", label: "إنهاء المهمة", icon: "ri-flag-line" },
];

const stageIndex: Record<Stage, number> = {
  not_started: 0,
  in_transit: 1,
  arrived: 2,
  executing: 3,
  completed: 4,
};

const stageMessage: Record<Stage, string> = {
  not_started: "لم يبدأ المنفّذ المهمة بعد. سيظهر التقدّم هنا فور انطلاقه.",
  in_transit: "المنفّذ في الطريق إلى موقع العميل.",
  arrived: "وصل المنفّذ لموقع العميل واستعد لبدء التنفيذ.",
  executing: "التنفيذ جارٍ الآن.",
  completed: "تم إنهاء المهمة بنجاح.",
};

const stageTone: Record<Stage, string> = {
  not_started: "bg-[#F4F8F8] text-ghasla-text-secondary",
  in_transit: "bg-[#FFF9EB] text-[#B45309]",
  arrived: "bg-[#E8F6F6] text-ghasla-text-link",
  executing: "bg-[#E8F6F6] text-ghasla-text-link",
  completed: "bg-[#E8F9F0] text-[#27AE60]",
};

const stageIcon: Record<Stage, string> = {
  not_started: "ri-time-line",
  in_transit: "ri-navigation-line",
  arrived: "ri-map-pin-2-line",
  executing: "ri-loader-4-line",
  completed: "ri-check-line",
};

function initialStage(order: Order): Stage {
  if (order.tab !== "current") return "completed";
  const s = order.statusLabel ?? "";
  if (s.includes("في الطريق") || s.includes("انطلق")) return "in_transit";
  if (s.includes("قيد") || s.includes("وصل")) return "executing";
  return "not_started";
}

export default function ExecutionFlow({ order }: { order: Order }) {
  const [stage] = useState<Stage>(() => initialStage(order));
  const currentIndex = stageIndex[stage];

  const isLaundry = order.serviceType === "مغاسل ملابس";

  return (
    <div className="rounded-[20px] border border-[#F0F4F4] bg-white p-4 shadow-card md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ghasla-text-heading">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0FDFD] text-ghasla-text-link">
            <i className="ri-route-line text-sm" />
          </span>
          تتبع تنفيذ المهمة
        </h3>
        <BadgeStage stage={stage} />
      </div>

      {/* Stepper */}
      <div className="mt-5 flex items-start">
        {stages.map((s, i) => {
          const idx = i + 1;
          const isDone = idx < currentIndex;
          const isCurrent = idx === currentIndex;
          return (
            <div key={s.key} className="flex flex-1 items-start">
              <div className="flex flex-col items-center gap-2">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-colors ${
                    isDone
                      ? "border-[#27AE60] bg-[#27AE60] text-white"
                      : isCurrent
                      ? "border-[#00AAB7] bg-[#00AAB7] text-white"
                      : "border-[#E4F5F5] bg-white text-ghasla-text-secondary"
                  }`}
                >
                  <i className={`${isDone ? "ri-check-line" : s.icon} text-sm`} />
                </span>
                <span
                  className={`whitespace-nowrap text-center text-xs font-medium ${
                    isCurrent
                      ? "text-ghasla-text-link"
                      : isDone
                      ? "text-[#27AE60]"
                      : "text-ghasla-text-secondary"
                  }`}
                >
                  {s.label}
                </span>
              </div>
              {i < stages.length - 1 && (
                <span
                  className={`mt-[17px] h-[2px] flex-1 rounded-full ${
                    idx < currentIndex ? "bg-[#27AE60]" : "bg-[#E4F5F5]"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Status — للاطّلاع فقط */}
      <div className="mt-5 border-t border-[#F0F4F4] pt-4">
        <div className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${stageTone[stage]}`}>
          <i className={`${stageIcon[stage]} text-lg ${stage === "executing" ? "animate-spin" : ""}`} />
          {stageMessage[stage]}
        </div>

        <p className="mt-3 flex items-center gap-1.5 text-xs text-ghasla-text-secondary">
          <i className="ri-information-line" />
          {isLaundry
            ? "هذا عرض متابعة فقط — تحديث المراحل والصور يتم من تطبيق مندوب التسليم."
            : "هذا عرض متابعة فقط — تحديث المراحل والصور يتم من تطبيق المنفّذ."}
        </p>
      </div>
    </div>
  );
}

function BadgeStage({ stage }: { stage: Stage }) {
  const map: Record<Stage, { label: string; tone: "gray" | "amber" | "teal" | "green"; pulse?: boolean }> = {
    not_started: { label: "لم تبدأ", tone: "gray" },
    in_transit: { label: "في الطريق", tone: "amber", pulse: true },
    arrived: { label: "وصل للعميل", tone: "teal" },
    executing: { label: "قيد التنفيذ", tone: "teal", pulse: true },
    completed: { label: "مكتمل", tone: "green" },
  };
  const m = map[stage];
  const cls =
    m.tone === "amber"
      ? "bg-[#FFF9EB] text-[#B45309]"
      : m.tone === "teal"
      ? "bg-[#E8F6F6] text-ghasla-text-link"
      : m.tone === "green"
      ? "bg-[#E8F9F0] text-[#27AE60]"
      : "bg-[#F4F8F8] text-ghasla-text-secondary";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${cls}`}>
      {m.pulse && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
      {m.label}
    </span>
  );
}